// Varias lecturas a la vez, sin que se pueda tirar el error sin querer.
//
// GEMELO de `app/src/lib/leerVarias.ts`, y desde el grupo B lo vigila
// `scripts/guard-gemelos-catalogo.mjs`. Dos copias y no una compartida porque
// los dos repos no comparten paquete: `admin` es Vite y `app` es Expo, con su
// propio tsconfig y su propio `node_modules`. La lógica son 15 líneas sin
// dependencias; el día que haya un paquete común, se unen.
//
// `listaEnCastellano` vivía en AlumnoDetallePage.tsx, que es donde se escribió.
// Se movió aquí porque una pantalla no es sitio del que importar utilidades: se
// arrastra el `default export` de la página entera, con sus efectos y sus
// imports, a cualquiera que solo quiera unir tres palabras. Esa historia es de
// este repo, y por eso está en la cabecera: es lo único que el guard permite
// que difiera.
//
// POR QUÉ EXISTE
// --------------
// postgrest-js NO RECHAZA NUNCA: envuelve la petición en un `.catch` propio y
// devuelve `{ data: null, error }` mientras `shouldThrowOnError` sea false
// (2.105.1, `index.cjs:292`). O sea que la forma de abajo, que está 88 veces
// en este repo y en el de la app, no es descuido de nadie: es lo que sale
// natural, y el lenguaje no avisa.
//
//   const [{ data: a }, { data: b }] = await Promise.all([...]);
//
// El `error` se cae en el propio destructuring y un fallo de RLS, un 300
// PGRST201 o una red caída llegan a la pantalla como "no hay nada". Ha morido
// tres veces en producción: el BUG 9 (la PF regalada), el BUG 13 (el chat
// vacío, dos semanas) y el #496 (/pedidos con pedidos dentro).
//
// Con `leerVarias` el `error` no está en una posición que se pueda omitir: es
// el resultado. Y cada consulta trae su nombre, que es lo que hace falta para
// contar CUÁL no se pudo leer en vez de un "algo ha fallado".
//
// CÓMO SE USA
// -----------
//   const { datos, fallidos, error } = await leerVarias([
//     { que: 'los bonos', consulta: supabase.from('user_credits').select('*') },
//     { que: 'las evaluaciones', consulta: supabase.from('formal_evaluations').select('*') },
//   ]);
//   const [bonos, evaluaciones] = datos;
//
//   · Todo o nada  →  if (error) return setErrorCarga(error);
//   · Por bloques  →  setBloquesFallidos(fallidos);  y el aviso de
//                     AlumnoDetallePage.tsx con listaEnCastellano(fallidos).
//
// `datos[i]` conserva el tipo de la consulta i: la tupla se mapea, no se
// aplana a un array de unión.

/** La forma de una respuesta de Supabase, en lo que nos importa de ella. */
type Respuesta<T> = { data: T; error: { message: string } | null };

export type Entrada<T> = {
  /**
   * Qué se está leyendo, en minúscula y con su artículo: "los bonos", "el
   * historial de sesiones". Va dentro de una frase ("No se ha podido cargar
   * …"), así que cada nombre trae el suyo porque no todos llevan el mismo.
   *
   * El artículo lo elige cada pantalla: el panel habla de un tercero ("los
   * bonos del alumno") y la app le habla al dueño ("tus bonos"). El texto de
   * este módulo no impone ninguno de los dos.
   */
  que: string;
  consulta: PromiseLike<Respuesta<T>>;
};

export type ResultadoVarias<T extends readonly Entrada<unknown>[]> = {
  /** Un elemento por consulta, en el mismo orden y con su propio tipo. */
  datos: { [K in keyof T]: T[K] extends Entrada<infer D> ? D : never };
  /** Los `que` de las consultas que no respondieron. Vacío si fue todo bien. */
  fallidos: string[];
  /**
   * El mensaje del primer error, o null. Para las pantallas de todo-o-nada,
   * que son las que no tienen nada que pintar si falla una parte.
   */
  error: string | null;
};

export async function leerVarias<T extends readonly Entrada<unknown>[]>(
  entradas: readonly [...T]
): Promise<ResultadoVarias<T>> {
  const respuestas = await Promise.all(entradas.map((e) => e.consulta));
  const fallidos: string[] = [];
  let error: string | null = null;
  respuestas.forEach((r, i) => {
    if (r.error) {
      fallidos.push(entradas[i].que);
      if (error === null) error = r.error.message || 'Error desconocido';
    }
  });
  return {
    datos: respuestas.map((r) => r.data) as ResultadoVarias<T>['datos'],
    fallidos,
    error,
  };
}

// "los bonos y las evaluaciones", no "bonos, evaluaciones": el aviso lo lee
// una persona, no un log.
export function listaEnCastellano(items: string[]): string {
  if (items.length === 0) return '';
  if (items.length === 1) return items[0];
  return `${items.slice(0, -1).join(', ')} y ${items[items.length - 1]}`;
}
